import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { apiUrl } from '../config';

// Session state for the whole app.
//
// The session itself lives in an httpOnly cookie the browser sends
// automatically — this context holds only the *derived* answer to "who is
// signed in", never a token. There is deliberately nothing here to persist to
// localStorage: if there were, XSS could read it.
//
// Every request therefore needs `credentials: 'include'`, or the cookie is not
// sent and a signed-in user looks signed out.

const AuthContext = createContext(null);

/** Status is a small state machine rather than a pair of booleans, so
 *  "still checking" can never be confused with "signed out" — that
 *  distinction is what stops the login page flashing on every load. */
export const AUTH_STATUS = {
  CHECKING: 'checking',
  SIGNED_IN: 'signed-in',
  SIGNED_OUT: 'signed-out',
};

export function AuthProvider({ children }) {
  const [status, setStatus] = useState(AUTH_STATUS.CHECKING);
  const [user, setUser] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const response = await fetch(apiUrl('auth/me'), {
        credentials: 'include',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) {
        setUser(null);
        setStatus(AUTH_STATUS.SIGNED_OUT);
        return;
      }
      const body = await response.json();
      setUser(body.user ?? null);
      setStatus(body.authenticated ? AUTH_STATUS.SIGNED_IN : AUTH_STATUS.SIGNED_OUT);
    } catch {
      // Network failure is indistinguishable from signed-out here, and
      // treating it as signed-out is the safe direction: the API will reject
      // an unauthenticated request anyway.
      setUser(null);
      setStatus(AUTH_STATUS.SIGNED_OUT);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  /** Full page navigation, not client-side routing: the OAuth handshake has to
   *  leave our origin entirely and come back. */
  const signIn = useCallback((returnTo) => {
    const target = returnTo || window.location.pathname;
    window.location.href = `${apiUrl('auth/google')}?returnTo=${encodeURIComponent(target)}`;
  }, []);

  const signOut = useCallback(async () => {
    try {
      await fetch(apiUrl('auth/logout'), { method: 'POST', credentials: 'include' });
    } finally {
      // Clear local state even if the request failed — the cookie may well be
      // gone, and leaving a stale "signed in" UI is worse than an extra 401.
      setUser(null);
      setStatus(AUTH_STATUS.SIGNED_OUT);
    }
  }, []);

  const value = {
    status,
    user,
    isSignedIn: status === AUTH_STATUS.SIGNED_IN,
    isChecking: status === AUTH_STATUS.CHECKING,
    signIn,
    signOut,
    refresh,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used inside an AuthProvider (see src/Main.js)');
  }
  return context;
}
